import { DownOutlined, InboxOutlined, UploadOutlined, UpOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import { Alert, Button, Card, message, Modal, Space, Tooltip, Typography, Upload } from 'antd';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

import { apiUrl } from '../../common/url';
import { useDataCollBlockedStatus } from '../../hooks/useDataCollBlockedStatus';

import { extractYear } from './extractYear';
import { DataCollBlockedStatus } from './types';

const { Dragger } = Upload;

interface DataCollBasisUploadProps {
  dataCollName: string;
  client: { id: string; token: string };
  setDataCollBlockedStatus: React.Dispatch<React.SetStateAction<DataCollBlockedStatus | undefined>>;
  setHasBasisUploadTable: React.Dispatch<React.SetStateAction<boolean>>;
  hasBasisUploadTable: boolean;
  style?: React.CSSProperties;
  onUploadSuccess?: () => void;
  dataCollBlockedStatus?: DataCollBlockedStatus;
  setHasTableDiffs?: React.Dispatch<React.SetStateAction<boolean>>;
  setAllStructuralMappingsApplied?: React.Dispatch<React.SetStateAction<boolean>>;
  hasTableDiffs?: boolean;
  expertModeActive?: boolean;
  handleGenerate: () => Promise<any>; // Required - parent must provide
  clearTransformationStatus?: () => void;
}

export const DataCollBasisUpload: React.FC<DataCollBasisUploadProps> = ({
  dataCollName,
  client,
  setDataCollBlockedStatus,
  setHasBasisUploadTable,
  hasBasisUploadTable,
  style,
  onUploadSuccess,
  dataCollBlockedStatus,
  setHasTableDiffs,
  setAllStructuralMappingsApplied,
  hasTableDiffs,
  expertModeActive = true,
  handleGenerate,
  clearTransformationStatus,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const { updateBlockedStatus } = useDataCollBlockedStatus({
    dataCollName,
    client,
    setDataCollBlockedStatus,
  });

  // handleGenerate is now always provided by parent component
  const [isExpanded, setIsExpanded] = useState(!hasBasisUploadTable);

  useEffect(() => {
    setIsExpanded(!hasBasisUploadTable);
  }, [hasBasisUploadTable]);

  // --- API CALL HELPERS ---
  const uploadBasis = (formData: FormData) => {
    return axios.post(
      `${process.env.REACT_APP_API_URL}/b2zero/${client.id}/upload_data_coll_basis`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${client.token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  };

  const handleUpload = () => {
    if (!client?.id || !dataCollName || fileList.length === 0) return;

    const year = extractYear(dataCollName);
    if (!year) {
      message.error('Jahr konnte nicht aus dem Namen der Datensammlung extrahiert werden');
      return;
    }

    const formData = new FormData();
    const file = fileList[0].originFileObj;
    if (!file) {
      message.error('Keine Datei zum Hochladen ausgewählt');
      return;
    }
    formData.append('file', file);
    formData.append('year', year.toString());

    setUploading(true);

    uploadBasis(formData)
      .then(() => {
        message.success('Stammdatentabelle erfolgreich hochgeladen', 6);
        setFileList([]);
        updateBlockedStatus(true);
        setHasBasisUploadTable(true);
        if (typeof onUploadSuccess === 'function') {
          onUploadSuccess();
        }

        // Auto-start transformation in client mode
        if (!expertModeActive) {
          handleGenerate().catch((error) => {
            // handleGenerate already shows user notification via message.error()
            // Just log for debugging - user has already been notified
            console.error('Auto-transformation failed:', error);
          });
        }
      })
      .catch((error) => {
        if (error.response) {
          if (error.response.status === 403) {
            message.error(
              'Keine Berechtigung zum Hochladen. Bitte prüfen Sie Ihre Zugriffsrechte.',
              6
            );
            Sentry.captureException(error);
          } else if (error.response.status === 409) {
            message.error(
              'Eine hochgeladene Stammdatentabelle existiert bereits für diese Stammdatentabelle',
              6
            );
            // Not tracked: expected user error
          } else {
            let errorMessage =
              error.response.data?.error ||
              error.response.data?.message ||
              'Fehler beim Hochladen der Stammdatentabelle';
            if (typeof errorMessage === 'object' && errorMessage !== null) {
              errorMessage = errorMessage.message || JSON.stringify(errorMessage);
            }

            // Check if this is a constraint validation error
            if (
              typeof errorMessage === 'string' &&
              errorMessage.includes('Constraint validation failed')
            ) {
              // Show constraint validation errors in a Modal (user must dismiss)
              Modal.error({
                title: 'Validierungsfehler',
                content: (
                  <div>
                    <p style={{ marginBottom: 12 }}>
                      Die hochgeladene Datei enthält ungültige Daten:
                    </p>
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        background: '#f5f5f5',
                        padding: 12,
                        borderRadius: 4,
                        maxHeight: 400,
                        overflow: 'auto',
                      }}
                    >
                      {errorMessage}
                    </pre>
                  </div>
                ),
                width: 900,
                okText: 'Verstanden',
              });
              // Not tracked: expected user data quality error
            } else {
              // Show other errors as regular message
              message.error(errorMessage, 6);
              Sentry.captureException(error);
            }
          }
        } else if (error.request) {
          message.error(
            'Keine Antwort vom Server erhalten. Bitte überprüfen Sie Ihre Netzwerkverbindung.',
            6
          );
          Sentry.captureException(error);
        } else {
          message.error('Unbekannter Fehler beim Hochladen der Stammdatentabelle', 6);
          Sentry.captureException(error);
        }
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const draggerProps: UploadProps = {
    multiple: false,
    fileList,
    maxCount: 1,
    accept: '.xlsx,.xls,.csv',
    beforeUpload: (file: RcFile) => {
      const isExcel =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/csv';
      if (!isExcel) {
        message.error('Nur Excel- oder CSV-Dateien sind erlaubt!');
        return false;
      }
      setFileList([
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          originFileObj: file,
        },
      ]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
    onDrop: (e) => {
      // Optionally handle drop event
      // console.log('Dropped files', e.dataTransfer.files);
    },
    showUploadList: true,
    listType: 'text',
  };

  const handleDeleteUploadTable = () => {
    if (!dataCollName) return;

    Modal.confirm({
      title: 'Stammdatentabelle löschen',
      content: (
        <div>
          <p style={{ color: '#ff4d4f', marginBottom: 16 }}>
            ⚠️ Warnung: Diese Aktion kann nicht rückgängig gemacht werden!
          </p>
          <p>{`Möchten Sie die Stammdatentabelle "${dataCollName}_basis_upload" wirklich löschen?`}</p>
          <p>Dies löscht auch alle zugehörigen Mappings und Differenzen.</p>
        </div>
      ),
      okText: 'Ja, löschen',
      cancelText: 'Abbrechen',
      okButtonProps: { danger: true },
      onOk: () => {
        return axios
          .post(
            `${apiUrl}/b2zero/${client.id}/delete_upload_table`,
            {
              table_name: `${dataCollName}_basis_upload`,
            },
            {
              headers: { Authorization: `Bearer ${client.token}` },
            }
          )
          .then(() => {
            return axios.post(
              `${apiUrl}/b2zero/${client.id}/delete_table_mappings`,
              {
                table_name: `${dataCollName}_basis`,
              },
              {
                headers: { Authorization: `Bearer ${client.token}` },
              }
            );
          })
          .then(() => {
            // Delete table diffs if they exist
            if (hasTableDiffs) {
              return axios.post(
                `${apiUrl}/b2zero/${client.id}/delete_table_diffs`,
                {
                  table_name: `${dataCollName}_basis`,
                },
                {
                  headers: { Authorization: `Bearer ${client.token}` },
                }
              );
            }
          })
          .then(() => {
            message.success('Die hochgeladene Stammdatentabelle wurde erfolgreich gelöscht');
            console.log(
              `uploaded table ${dataCollName}_basis_upload, structure mappings and diffs deleted`
            );
            setHasBasisUploadTable(false);
            if (setHasTableDiffs) setHasTableDiffs(false);
            if (setAllStructuralMappingsApplied) setAllStructuralMappingsApplied(false);
            if (clearTransformationStatus) clearTransformationStatus();

            // Unblock the table after successful deletion
            updateBlockedStatus(false).catch((error) => {
              console.error('Failed to unblock after deleting upload table:', error);
            });
          })
          .catch((error) => {
            message.error('Fehler beim Löschen der Daten: ' + error.message);
            Sentry.captureException(error);
          });
      },
    });
  };

  return (
    <Card
      title={
        <Typography.Title
          level={3}
          style={{
            cursor: hasBasisUploadTable ? 'pointer' : 'default',
            margin: 0,
          }}
          onClick={hasBasisUploadTable ? () => setIsExpanded((prev) => !prev) : undefined}
        >
          Stammdatentabelle Uploadbereich
        </Typography.Title>
      }
      style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      bordered
      extra={
        hasBasisUploadTable && (
          <Button
            type="text"
            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setIsExpanded((prev) => !prev)}
          />
        )
      }
    >
      {hasBasisUploadTable && (
        <Alert
          message={`Hochgeladene Stammdatentabelle vorhanden: ${dataCollName}_basis_upload`}
          type="success"
          showIcon
          action={
            (!dataCollBlockedStatus ||
              dataCollBlockedStatus.isBlocked === false ||
              dataCollBlockedStatus?.blocking_user_is_current_user) && (
              // originally: Upload Tabelle & Abgleiche löschen
              <Button size="small" danger onClick={handleDeleteUploadTable}>
                Hochgeladene Stammdatentabelle löschen
              </Button>
            )
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {(!hasBasisUploadTable || isExpanded) && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Dragger {...draggerProps} style={{ width: '100%' }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              <b>Stammdatentabelle für das gewählte Bilanzierungsjahr hochladen</b>
            </p>
            <p className="ant-upload-hint">
              <b>Schritt 1:</b> Datei per Klick oder Drag & Drop auswählen.
              <br />
              <b>Schritt 2:</b> Klicken Sie auf{' '}
              <b>
                {expertModeActive
                  ? 'Stammdatentabelle hochladen'
                  : 'Stammdatentabelle hochladen & verarbeiten'}
              </b>
              , um den Upload zu starten{expertModeActive ? '.' : 'und zu verarbeiten'}
              <br />
              <br />
              Unterstützte Dateiformate: <b>Excel (.xlsx, .xls)</b> oder <b>CSV</b>.<br />
              Die Datei muss die gleiche Struktur wie die Tabelle <b>{dataCollName}_basis</b> haben.
            </p>
          </Dragger>
          <Upload
            {...draggerProps}
            showUploadList={false}
            style={{ display: 'block', margin: '0 auto' }}
          >
            <Button icon={<UploadOutlined />}>Datei auswählen</Button>
          </Upload>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              // originally: "Eine Upload Tabelle existiert bereits. Löschen Sie die Upload Tabelle und die zugehörigen Abgleiche,um eine neue hochzuladen."
              title="Eine hochgeladene Stammdatentabelle existiert bereits. Löschen Sie die hochgeladene Stammdatentabelle um eine neue hochzuladen."
              placement="top"
            >
              <Button
                type="primary"
                onClick={handleUpload}
                disabled={fileList.length === 0 || hasBasisUploadTable}
                loading={uploading}
              >
                {uploading
                  ? 'Wird hochgeladen...'
                  : expertModeActive
                    ? 'Stammdatentabelle hochladen'
                    : 'Stammdatentabelle hochladen & verarbeiten'}
              </Button>
            </Tooltip>
            {hasBasisUploadTable &&
              (!dataCollBlockedStatus ||
                dataCollBlockedStatus.isBlocked === false ||
                dataCollBlockedStatus?.blocking_user_is_current_user) && (
                <Button danger onClick={handleDeleteUploadTable} style={{ marginLeft: 8 }}>
                  Hochgeladene Stammdatentabelle löschen
                </Button>
              )}
          </div>
        </Space>
      )}
    </Card>
  );
};
